import React, {
  MouseEvent,
  useContext,
  useRef,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import {
  notEmptyString,
} from '@populateui/utils';
import clsx from 'clsx';
import startCase from 'lodash/startCase';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import Tag from 'core/components/Tag';
import ToolTip from 'core/components/Tooltip';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  DataContext,
} from 'core/context/data';
import {
  useMedia,
  useNotify,
} from 'core/hooks';
import {
  RTMPatientRowDTO,
  RTMUserDTO,
} from 'dtos';
import {
  RTMMonitoringPriority,
} from 'dtos/rTMMonitoringPriority';
import Button from 'pages/Dashboard/components/Button';
import Name from 'pages/Dashboard/components/Name';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import Spinner from 'pages/Dashboard/components/Spinner';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  useMarkPatientReviewed,
} from 'pages/Dashboard/pages/Dashboard/RTM/hooks';
import {
  MetricTone,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/constants';
import {
  getPriorityTone,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/helpers';
import {
  getDisplayName,
  getInitials,
} from 'pages/Dashboard/utils/helper';
import {
  formatDate,
} from 'utils/date';
import dayjs from 'utils/dayjs';
import {
 useNavigate,
} from 'react-router-dom';
import isNil from 'lodash/isNil';

type PatientCellProps = {
  row: Row<RTMPatientRowDTO>;
};

type ReasonCellProps = {
  reason?: string | null;
};

type PriorityCellProps = {
  priority?: RTMMonitoringPriority;
  isReviewed?: boolean;
  orderingProvider?: RTMUserDTO;
};

type ReviewedCellProps = {
  row: Row<RTMPatientRowDTO>;
};

const PRIORITY_DOT_CLASSES: Record<MetricTone, string> = {
  default: 'bg-gray-400',
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
};

function formatCheckInDate(value?: string | null): string {
  if (!notEmptyString(value)) return DEFAULT_EMPTY_VALUE;
  const date = dayjs(value);
  if (!date.isValid()) return DEFAULT_EMPTY_VALUE;
  if (date.isSame(dayjs(), 'day')) return 'Today';
  if (date.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday';
  return formatDate(date);
}

function ReasonCell({ reason }: ReasonCellProps) {
  const reasonRef = useRef<HTMLSpanElement>(null);
  const label = notEmptyString(reason) ? reason : DEFAULT_EMPTY_VALUE;

  return (
    <ToolTip title={label} auto ref={reasonRef}>
      <span
        ref={reasonRef}
        className="text-xs font-medium text-gray-700 truncate"
      >
        {label}
      </span>
    </ToolTip>
  );
}

function PatientCell({ row }: PatientCellProps) {
  const { getMediaPathById } = useMedia();
  const {
    patientId,
    formattedNames,
  } = row.original?.patient ?? {};
  const name = getDisplayName(formattedNames, DEFAULT_EMPTY_VALUE);
  const initials = getInitials(formattedNames);
    const navigate = useNavigate();
    const link = getPatientChartPath(patientId);

  return (
    <Name
      link={{
        onClick: (e: MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation();
          if (!isNil(link)) {
            navigate(link);
          }
        },
      }}
      name={name}
      initials={initials}
      labelClassName="text-xs font-semibold truncate text-gray-700"
      image={{
        src: getMediaPathById(patientId ?? 0) ?? undefined,
        size: 'regular',
      }}
    >
      <ToolTip title={`Enrolled on ${formatDate(row.original?.enrolledOn ?? '')}`}>
        <span className="text-xs font-medium text-gray-700 truncate w-fit">
          <span className="text-gray-500">Enrolled on</span> {formatDate(row.original?.enrolledOn ?? '')}
        </span>
      </ToolTip>
    </Name>
  );
}

function PriorityCell({ priority, isReviewed, orderingProvider }: PriorityCellProps) {
  const userInfo = useUserInfo();
  const isUserProvider = orderingProvider?.userId === userInfo?.user?.userId;
  const label = priority ? startCase(priority) : '';
  const orderingProviderName = getDisplayName(
    orderingProvider?.formattedNames,
    DEFAULT_EMPTY_VALUE,
  );

  return (
    <Tag
      tagName={label}
      className="!px-2 !h-6 py-1 font-semibold inline-flex items-center gap-1"
      tooltip={isUserProvider
        ? undefined
        : `Based on threshold settings from ${orderingProviderName}`}
    >
      {!isReviewed && isUserProvider && (
        <span
          className={clsx(
            'w-1.5 h-1.5 rounded-full shrink-0',
            PRIORITY_DOT_CLASSES[getPriorityTone(priority)],
          )}
        />
      )}
      {label}
    </Tag>
  );
}

function ReviewedCell({ row }: ReviewedCellProps) {
  const { isReviewed, patient, reviewedOn, reviewedBy } = row.original ?? {};
  const src = useUserProfilePicturePath(reviewedBy?.userId);
  const notify = useNotify();
  const { refetch } = useContext(DataContext);
  const {
    mutateAsync: markPatientReviewed,
    isPending,
  } = useMarkPatientReviewed();

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      await markPatientReviewed({ patientId: patient?.patientId ?? 0 });
      await refetch?.();
      notify({
        variant: 'success',
        message: 'Patient marked as reviewed',
      });
    } catch (error) {
      console.error(error);
    }
  };

  return isReviewed
    ? (<Name
          name={getDisplayName(reviewedBy?.formattedNames, DEFAULT_EMPTY_VALUE)}
          initials={getInitials(reviewedBy?.formattedNames)}
          image={{ size: 'xs', src }}
        >
          <span className="text-xs font-medium text-gray-700 truncate w-fit">
            <span className="text-gray-500">Reviewed on</span> {formatDate(reviewedOn ?? '')}
          </span>
        </Name>)
    : (
      <ToolTip title="Mark as Reviewed">
        <Button
          className={clsx("!px-2 !py-0.5 !text-xs flex items-center gap-1 !border-blue-500", {
            '!border-gray-200 !text-gray-500': isPending,
            '!border-blue-500 !text-blue-500': !isPending,
          })}
          variant="outlined"
          color="primary"
          disabled={isPending}
          onClick={handleClick}
        >
          {isPending
            ? <Spinner size={12} />
            : <CheckCircleOutlinedIcon className="!w-4 !h-4" />}
          Mark as Reviewed
        </Button>
      </ToolTip>
    );
}

type OrderingProviderCellProps = {
  orderingProvider?: RTMUserDTO;
  qualifiedOn?: string | null;
};

function OrderingProviderCell({ orderingProvider, qualifiedOn }: OrderingProviderCellProps) {
  const src = useUserProfilePicturePath(orderingProvider?.userId);

  return (
    <Name
      name={getDisplayName(orderingProvider?.formattedNames, DEFAULT_EMPTY_VALUE)}
      initials={getInitials(orderingProvider?.formattedNames)}
      image={{ size: 'xs', src }}
    >
      <span className="text-xs font-medium text-gray-700 truncate">
        <span className="text-gray-500">Approved on</span> {formatDate(qualifiedOn ?? '')}
      </span>
    </Name>
  );
}

const columns: ColumnDef<RTMPatientRowDTO>[] = [{
  id: 'patient',
  header: 'Patient',
  minSize: 220,
  cell: ({ row }) => <PatientCell row={row} />,
}, {
  id: 'priority',
  header: 'Status',
  size: 150,
  cell: ({ row: { original: { priority, isReviewed, orderingProvider } } }) => (
    <PriorityCell
      priority={priority}
      isReviewed={isReviewed}
      orderingProvider={orderingProvider}
    />
  ),
}, {
  id: 'reason',
  header: 'Reason',
  meta: { autoSized: true },
  minSize: 120,
  cell: ({ row: { original: { reason } } }) => (
    <ReasonCell reason={reason} />
  ),
}, {
  id: 'latestCheckInDate',
  header: 'Checked-in',
  size: 180,
  cell: ({ row: { original: { latestCheckInDate } } }) => (
    <span className="text-xs font-medium text-gray-700 truncate">{formatCheckInDate(latestCheckInDate)}</span>
  ),
}, {
  id: 'orderingProvider',
  header: 'Ordering Provider',
  size: 220,
  cell: ({ row: { original: { orderingProvider, qualifiedOn } } }) => (
    <OrderingProviderCell
      orderingProvider={orderingProvider}
      qualifiedOn={qualifiedOn}
    />
  ),
}, {
  id: 'isReviewed',
  header: 'Reviewed',
  size: 220,
  cell: ({ row }) => <ReviewedCell row={row} />,
}];

export default columns;
